import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Le consentement est décoché au départ et la case est obligatoire : le RGPD
// interdit une case pré-remplie, et le serveur refuse l'inscription sans elle.
export default function NewsletterForm() {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [consentement, setConsentement] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [inscrit, setInscrit] = useState(false);
  const [erreur, setErreur] = useState('');

  const fr = language === 'fr';

  const envoyer = async (e) => {
    e.preventDefault();
    setErreur('');

    if (!consentement) {
      setErreur(fr
        ? 'Veuillez cocher la case pour accepter de recevoir nos emails.'
        : 'Please tick the box to agree to receive our emails.');
      return;
    }

    setEnvoi(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        consent: true,
        language,
        origin_url: window.location.origin,
      });
      setInscrit(true);
      setEmail('');
      setConsentement(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setErreur(
        typeof detail === 'string'
          ? detail
          : fr
            ? "L'inscription n'a pas pu être enregistrée. Vérifiez votre adresse et réessayez."
            : 'We could not save your subscription. Please check your address and try again.'
      );
    } finally {
      setEnvoi(false);
    }
  };

  if (inscrit) {
    return (
      <div
        className="flex items-center justify-center gap-2 text-green-700 font-medium"
        role="status"
        data-testid="newsletter-success"
      >
        <Check className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm sm:text-base">
          {fr
            ? 'Merci ! Un email de confirmation vient de vous être envoyé.'
            : 'Thank you! A confirmation email is on its way.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={envoyer} className="text-left max-w-xl mx-auto" data-testid="newsletter-form">
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
        <label htmlFor="newsletter-email" className="sr-only">
          {fr ? 'Votre adresse email' : 'Your email address'}
        </label>
        <input
          id="newsletter-email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={fr ? 'Votre email' : 'Your email'}
          className="px-5 py-3 sm:px-6 sm:py-4 rounded-full border-2 border-border focus:border-primary outline-none text-sm sm:text-base flex-1"
          data-testid="newsletter-email-input"
        />
        <button
          type="submit"
          disabled={envoi}
          className="flex items-center justify-center gap-2 px-6 py-3 sm:px-8 sm:py-4 bg-primary text-white rounded-full font-semibold text-sm sm:text-base hover:bg-primary_hover transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="newsletter-submit"
        >
          {envoi && <Loader2 className="w-4 h-4 animate-spin" />}
          {envoi ? (fr ? 'Envoi…' : 'Sending…') : (fr ? "S'inscrire" : 'Subscribe')}
        </button>
      </div>

      <div className="mt-4 flex items-start gap-2.5">
        <input
          id="newsletter-consent"
          type="checkbox"
          checked={consentement}
          onChange={(e) => setConsentement(e.target.checked)}
          className="mt-0.5 w-4 h-4 flex-shrink-0 accent-primary cursor-pointer"
          data-testid="newsletter-consent"
        />
        <label htmlFor="newsletter-consent" className="text-xs text-text_secondary leading-relaxed cursor-pointer">
          {fr ? (
            <>
              J'accepte de recevoir les nouveautés et offres d'AfriOne BD par email. Mon adresse
              ne sert qu'à cet envoi, n'est transmise à personne, et je peux me désinscrire à tout
              moment via le lien présent dans chaque message.{' '}
              <Link to="/confidentialite" className="text-primary hover:underline">
                Politique de confidentialité
              </Link>
            </>
          ) : (
            <>
              I agree to receive news and offers from AfriOne BD by email. My address is used only
              for this purpose, is never shared, and I can unsubscribe at any time via the link in
              every message.{' '}
              <Link to="/confidentialite" className="text-primary hover:underline">
                Privacy policy
              </Link>
            </>
          )}
        </label>
      </div>

      {erreur && (
        <p className="mt-3 text-sm text-red-600" role="alert" data-testid="newsletter-error">
          {erreur}
        </p>
      )}
    </form>
  );
}
